import { FoliarRosterRow } from '../../services/nutritionService';
import { naturalCompare } from '../../utils/naturalCompare';

export type RosterSortKey =
  | 'orchardNo' | 'cultivar' | 'areaHa' | 'waterL' | 'tanksPlanned' | 'tanksApplied' | 'status';

export interface RosterSort { key: RosterSortKey; direction: 'asc' | 'desc' }

export interface RosterTotals {
  orchards: number; areaHa: number; waterL: number; tanksPlanned: number; tanksApplied: number;
  /** How many of the summed rows contributed to `tanksPlanned` — see that
   * field's own comment. 0 means nothing in scope has a machine assigned,
   * which the caller must render as "no data", never as a bare 0. */
  tanksPlannedAssignedRows: number;
  /** How many of the summed rows contributed to `areaHa` / `waterL` — an
   * unknown orchard or unplanned session is excluded from both the sum and
   * this count. 0 means every row in scope is unknown, which the caller must
   * render as an absence (a dash), never as a bare 0 — this is the same
   * #460 guard `tanksPlannedAssignedRows` already provides, extended to the
   * other two nullable totals. */
  areaHaKnownRows: number;
  waterLKnownRows: number;
}

export interface RosterSummary {
  unassigned: number; awaitingSignoff: number; complete: number; cultivars: string[];
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** A session with no combo cannot be worked — nobody is assigned to it. */
export function isUnassigned(row: FoliarRosterRow): boolean {
  return row.combos.length === 0;
}

/** Completed work that still needs a manager's sign-off. */
export function isAwaitingSignoff(row: FoliarRosterRow): boolean {
  return row.status === 'completed' && !row.signedOffAt;
}

export function orderRoster(rows: FoliarRosterRow[], sort: RosterSort): FoliarRosterRow[] {
  const dir = sort.direction === 'asc' ? 1 : -1;
  // Uncaptured tanksApplied sorts last in BOTH directions: "not captured" is
  // not a small number, so it must never rank alongside zero.
  const nullsLast = (a: number | null, b: number | null): number | null => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return null;
  };
  return rows.slice().sort((a, b) => {
    switch (sort.key) {
      case 'cultivar': return naturalCompare(a.cultivar, b.cultivar) * dir;
      case 'status': return naturalCompare(a.status, b.status) * dir;
      // areaHa, waterL and tanksPlanned are all nullable (#460, Task 6/7) —
      // same treatment as tanksApplied below, through the SAME nullsLast
      // helper rather than a second one.
      case 'areaHa': {
        const n = nullsLast(a.areaHa, b.areaHa);
        if (n !== null) return n;
        return ((a.areaHa as number) - (b.areaHa as number)) * dir;
      }
      case 'waterL': {
        const n = nullsLast(a.waterL, b.waterL);
        if (n !== null) return n;
        return ((a.waterL as number) - (b.waterL as number)) * dir;
      }
      case 'tanksPlanned': {
        const n = nullsLast(a.tanksPlanned, b.tanksPlanned);
        if (n !== null) return n;
        return ((a.tanksPlanned as number) - (b.tanksPlanned as number)) * dir;
      }
      case 'tanksApplied': {
        const n = nullsLast(a.tanksApplied, b.tanksApplied);
        if (n !== null) return n;
        return ((a.tanksApplied as number) - (b.tanksApplied as number)) * dir;
      }
      default: return naturalCompare(a.orchardNo, b.orchardNo) * dir;
    }
  });
}

export function filterRoster(rows: FoliarRosterRow[], search: string, chip: string): FoliarRosterRow[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (q) {
      const hay = [r.orchardNo, r.cultivar, ...r.combos.map((c) => c.operatorName), ...r.combos.map((c) => c.machineName)]
        .join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (chip === 'unassigned') return isUnassigned(r);
    if (chip === 'awaiting') return isAwaitingSignoff(r);
    if (chip.startsWith('cultivar:')) return r.cultivar === chip.slice('cultivar:'.length);
    return true;
  });
}

export function rosterTotals(rows: FoliarRosterRow[]): RosterTotals {
  // Dose-C fix: `tanksPlanned` exists only once a machine is assigned — it is
  // a combo's `plannedTankCount`, written by assignCombo from that combo's
  // machine, and reported by the backend only when every planned combo on the
  // row shares ONE machine (D5; #461. See FoliarRosterRow.tankVolumeL's own
  // comment, the canonical explanation). An unassigned row has no count, so
  // only rows with a real machine (tankVolumeL != null) contribute here; the
  // rest are absent from the total, never a zero standing in for one.
  //
  // #460 (Task 7): tankVolumeL != null does NOT imply tanksPlanned != null —
  // a session can carry a resolvable machine on its reference combo while
  // ALSO having 2+ distinct machines among its planned combos (D5), which
  // makes tanksPlanned itself unknowable. Filtering on tankVolumeL alone let
  // such a row silently drop out of the SUM (`sum + null` coerces to `sum`)
  // while still being counted as "assigned" — inflating the assigned-row
  // count against a total that never grew. Both filters together fix that.
  const tanksPlannedKnownRows = rows.filter((r) => r.tankVolumeL != null && r.tanksPlanned != null);
  // #460 (Task 7 fix): filtered ONCE and reused for both the sum and the
  // known-row count, so the two can never disagree about which rows counted.
  const areaHaKnownRows = rows.filter((r) => r.areaHa != null);
  const waterLKnownRows = rows.filter((r) => r.waterL != null);
  return {
    // Counts, never fractional — no rounding needed.
    orchards: rows.length,
    // areaHectares is a float in the DB (live data holds 0.8600000000000001),
    // so a raw sum leaks float noise straight into the totals row.
    // #460: areaHa is nullable — an unknown orchard is EXCLUDED from the sum,
    // never added as a silent zero. `areaHaKnownRows` tells the caller when
    // EVERY row in scope is unknown, so it can render a dash instead of the
    // bare 0 an empty-array reduce would otherwise produce.
    areaHa: round2(areaHaKnownRows.reduce((s, r) => s + (r.areaHa as number), 0)),
    areaHaKnownRows: areaHaKnownRows.length,
    // Fix 1 addendum (whole-branch review): `waterL` (each row's
    // plannedMix.totalWaterL) is round2'd at ITS OWN source
    // (nutritionFoliarMix.js), but summing several already-rounded 2dp
    // floats can still reintroduce a long tail — the same class of bug as
    // areaHa above, just with the rounding one step further upstream.
    // FoliarRoundPage.tsx renders this total raw, with no formatter.
    // #460: waterL is nullable — an unplanned session is EXCLUDED, never 0.
    // Same `waterLKnownRows` dash guard as areaHa above.
    waterL: round2(waterLKnownRows.reduce((s, r) => s + (r.waterL as number), 0)),
    waterLKnownRows: waterLKnownRows.length,
    // `tanksPlanned` (a combo's `plannedTankCount`) is ALWAYS
    // Math.ceil(...) at its single writer — `buildPerTank` in
    // nutritionFoliarMix.js, called only by assignCombo — an integer, never a
    // fraction, so a sum of integers is exact in float64 and round2 here
    // would be a no-op. Left unrounded deliberately, not by oversight.
    // (The FRACTION is a separate field, `plannedTanksExact`, and it is not
    // what this column sums.)
    tanksPlanned: tanksPlannedKnownRows.reduce((s, r) => s + (r.tanksPlanned as number), 0),
    tanksPlannedAssignedRows: tanksPlannedKnownRows.length,
    // Fix 1 (whole-branch review): each row's tanksApplied is now round2'd
    // at the BACKEND (foliarRosterService.js), but re-summing several
    // already-rounded fractional values here can still land on a long float
    // (e.g. 1.1 + 2.2 rounds to 1.1 and 2.2 individually, and their sum is
    // still 3.3000000000000003) — this is the totals-row half of the same
    // defect the backend fix addresses; FoliarRoundPage.tsx renders it raw.
    tanksApplied: round2(rows.reduce((s, r) => s + (r.tanksApplied ?? 0), 0)),
  };
}

export function summariseRoster(rows: FoliarRosterRow[]): RosterSummary {
  return {
    unassigned: rows.filter(isUnassigned).length,
    awaitingSignoff: rows.filter(isAwaitingSignoff).length,
    complete: rows.filter((r) => r.status === 'signed_off').length,
    cultivars: Array.from(new Set(rows.map((r) => r.cultivar).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
  };
}
